import React, { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'

import styles from './Installation.module.css'
import CopyIcon from 'Icons/copy.svg'
import CopiedIcon from 'Icons/check.svg'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Button from 'Design/components/Button'
import SwitchInput from 'Design/components/SwitchInput'
import Textarea from 'Design/components/Textarea'
import HelpText from 'Design/components/HelpText'
import FormErrorText from 'Design/components/FormErrorText'

import { useUpdateBotProfileMutation } from 'Services/bot-profiles'

import useClipboard from 'Utils/hooks/useClipboard'
import { useBotSettingsContext } from '../SettingsContext'
import installFormValidationSchema from './install-form-validation-schema'

const CONFIG = process.env.CONFIG

const createInstallCode = (token) => {
  return `<!--Start of Bot Widget Install Script-->
<script>
    window.HFBOT_CONFIG = {
        EMBED_TOKEN: "${token}",
        options: {
            showBadge: true,
        },
    };
    (function () {
        const scriptTag = document.createElement("script");
        scriptTag.type = "text/javascript";
        scriptTag.async = true;
        scriptTag.defer = true;
        scriptTag.src = "${new URL('/js/widget-loader.js', CONFIG.WIDGET_ASSET_URL).href}";
        var e = document.body || document.head;
        e.appendChild(scriptTag);
    })();
</script>
<!--End of Bot Widget Install Script-->`
}

const Installation = () => {
  const { copy, copied } = useClipboard()
  const bot = useBotSettingsContext()

  const installCode = useMemo(() => createInstallCode(bot.embed_token), [bot.embed_token])
  const [updateBot, result] = useUpdateBotProfileMutation()

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors, isSubmitSuccessful, isDirty },
    reset,
    getValues
  } = useForm({
    defaultValues: {
      name: bot.name,
      enableBotWidget: bot.status === 'active'
    },
    resolver: yupResolver(installFormValidationSchema)
  })

  const onFormSubmit = (data) => {
    const payload = {
      id: bot.id,
      name: data.name,
      status: data.enableBotWidget ? 'active' : 'inactive'
    }

    const promise = updateBot(payload).unwrap()

    toast.promise(promise, {
      loading: 'Updating installation settings',
      success: 'Installation settings updated successfully',
      error: 'Error while updating installation settings'
    })

    return promise
  }

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return
    }

    reset({ ...getValues() })
  }, [isSubmitSuccessful, reset, getValues])

  const codeRows = installCode.split('\n').length
  const disableSubmitButton = isSubmitting || result.isLoading || !isDirty

  return (
    <div className={styles.Container}>
      <div>
        <form className={styles.Form} onSubmit={handleSubmit(onFormSubmit)}>
          <FormControl isInvalid={errors.name}>
            <Label>Bot Name</Label>
            <Input placeholder='Enter bot name' {...register('name')} />
            {errors.name && <FormErrorText>{errors.name.message}</FormErrorText>}
          </FormControl>

          <FormControl>
            <Controller
              name='enableBotWidget'
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <SwitchInput checked={value} onChange={onChange}>
                    Enable bot widget
                  </SwitchInput>
                )
              }}
            />
          </FormControl>

          <div className={styles.ButtonContainer}>
            <Button type='submit' variant='primary' disabled={disableSubmitButton}>
              Save
            </Button>
          </div>
        </form>
      </div>

      <div>
        <div className={styles.InstallHeading}>Installation Code</div>

        <div className={styles.CodeContainer}>
          <Textarea className={styles.Code} rows={codeRows} readOnly value={installCode} />
          <div role='button' className={styles.CopyButton} onClick={() => copy(installCode)}>
            <span className={styles.CopyIcon}>{copied ? <CopiedIcon /> : <CopyIcon />}</span>
            {copied ? 'Copied' : 'Copy Code'}
          </div>
        </div>
        <HelpText>
          To install the chatbot, paste the below install code before the closing {'</body>'} tag on your website's
          footer
        </HelpText>
      </div>
    </div>
  )
}

export default Installation
